import { ChevronRight, Scale } from 'lucide-react'
import { useState } from 'react'

import { formatLatestWeight, isEmptyHtml, type Patient } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { normaliseForDisplay } from '@/frontend/components/internal-notes-utils'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { ExpandableContent } from '@/frontend/components/ui/expandable-content'
import { RichTextContent } from '@/frontend/components/ui/rich-text-content'
import { UpdatePatientForm } from '@/frontend/features/patient/update-patient.form'
import { usePatientAttributes } from '@/frontend/features/patient/use-patient-attributes'
import { WeightChartDialog } from '@/frontend/features/patient/weight-chart-dialog'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListPatientVitals } from '@/frontend/hooks/api/patients/use-list-patient-vitals'
import { cn } from '@/frontend/lib/utils'

export function PatientDetailsCard({ patient }: { patient: Patient }) {
  const attributes = usePatientAttributes(patient)
  const [editOpen, setEditOpen] = useState(false)
  const [weightChartOpen, setWeightChartOpen] = useState(false)
  const { region } = useViewerRegion()
  const { data: vitals, isPending, isError } = useListPatientVitals(patient.id)
  const latestWeight = vitals?.find((entry) => entry.weight != null)
  const weight = formatLatestWeight(vitals, region)

  return (
    <>
      <Card>
        <CardHeader
          className="cursor-pointer"
          role="button"
          tabIndex={0}
          aria-label="Edit patient details"
          onClick={() => setEditOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setEditOpen(true)
            }
          }}
        >
          <CardTitle>Patient Details</CardTitle>
          <CardAction>
            <ChevronRight className="size-4 text-muted-foreground" />
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-sm">
            {(
              ['species', 'breed', 'sex', 'color', 'neuter', 'microchip', 'dobAndAge'] as const
            ).map((key) => {
              const { icon, label, value } = attributes[key]
              return (
                <span
                  key={key}
                  className={cn(
                    'flex min-w-0 items-center gap-1.5',
                    key === 'dobAndAge' ? 'col-span-2' : ''
                  )}
                >
                  <span title={label} className="shrink-0 [&>svg]:size-3.5 text-muted-foreground">
                    {icon}
                  </span>
                  <span className="sr-only">{label}: </span>
                  <span className="min-w-0 break-words text-foreground font-medium">{value}</span>
                </span>
              )
            })}
            <button
              type="button"
              className="group col-span-2 flex w-fit max-w-full cursor-pointer items-center gap-1.5 rounded-sm text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
              disabled={isPending || isError || !latestWeight}
              onClick={() => setWeightChartOpen(true)}
            >
              <span title="Weight" className="shrink-0">
                <Scale className="size-3.5 text-muted-foreground" aria-hidden="true" />
              </span>
              <span className="sr-only">Weight: </span>
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-1 gap-y-0.5">
                <span className={weight ? 'font-medium' : 'text-muted-foreground'}>
                  {isPending
                    ? 'Loading weight…'
                    : isError
                      ? 'Unable to load weight'
                      : (weight ?? 'No weights recorded')}
                </span>
                {latestWeight && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <FormattedDate value={latestWeight.createdAt} clinicId={null} />
                    <ChevronRight
                      className="size-3 shrink-0 motion-safe:transition-transform motion-safe:duration-150 motion-safe:group-hover:translate-x-0.5 motion-safe:group-focus-visible:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </span>
                )}
              </span>
              {latestWeight && <span className="sr-only">View weight history</span>}
            </button>
          </div>
          {!isEmptyHtml(patient.internalNotes) && (
            <section className="border-t pt-3">
              <ExpandableContent maxHeight={100}>
                <RichTextContent
                  html={normaliseForDisplay(patient.internalNotes ?? '')}
                  className="select-text break-words"
                />
              </ExpandableContent>
            </section>
          )}
        </CardContent>
      </Card>

      <WeightChartDialog
        vitals={vitals ?? []}
        open={weightChartOpen}
        onOpenChange={setWeightChartOpen}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen} disablePointerDismissal>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Edit Patient - {patient.name}</DialogTitle>
          </DialogHeader>
          <UpdatePatientForm
            patient={patient}
            onSuccess={() => setEditOpen(false)}
            onCancel={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
